/**
 * The iCalendar half of a repeat: RRULE, and the VTIMEZONE that makes an
 * attendee's calendar expand it the way ours does.
 *
 * `DTSTART:20260915T164500Z` plus `RRULE:FREQ=WEEKLY` tells the receiving
 * calendar to repeat every 168 hours EXACTLY, so a lunch set in summer shows up
 * an hour late all winter. The fix is a floating wall clock bound to a zone —
 * `DTSTART;TZID=America/Chicago:20260915T114500` — which is only legal if the
 * file also DEFINES that zone. Hence VTIMEZONE.
 */

import { businessDateParts } from "@west-tennessee-consulting/business-date";

import type { RecurrenceRule } from "./rule.js";
import { isRepeating } from "./rule.js";
import { resolveEventTimeZone } from "./expand.js";

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** `YYYYMMDDTHHMMSSZ` — an absolute instant. */
export function formatIcsUtc(isoUtc: string): string {
  const date = new Date(isoUtc);
  return (
    `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}` +
    `T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`
  );
}

/** `YYYYMMDDTHHMMSS` — a wall clock, meaningless without its TZID. */
export function formatIcsLocal(isoUtc: string, timeZone: string): string {
  const parts = businessDateParts(new Date(isoUtc), timeZone);
  return (
    `${parts.year}${pad2(parts.month)}${pad2(parts.day)}` +
    `T${pad2(parts.hour)}${pad2(parts.minute)}${pad2(parts.second)}`
  );
}

/**
 * RRULE body for an enabled rule, or undefined when it does not repeat.
 *
 * UNTIL is always emitted in UTC form, which RFC 5545 §3.3.10 REQUIRES when
 * DTSTART carries a TZID.
 */
export function buildRRule(
  rule: RecurrenceRule | undefined | null
): string | undefined {
  if (!isRepeating(rule)) return undefined;

  const segments = [
    `FREQ=${rule.frequency.toUpperCase()}`,
    `INTERVAL=${Math.max(1, Math.floor(rule.interval) || 1)}`,
    ...(rule.untilUtc ? [`UNTIL=${formatIcsUtc(rule.untilUtc)}`] : []),
  ];

  return segments.join(";");
}

/**
 * Read an RRULE back into a rule.
 *
 * Only the subset this system writes is understood; anything richer (BYDAY,
 * COUNT, a frequency we do not model) returns undefined, so an imported invite
 * is treated as a single event rather than silently repeated on the wrong days.
 */
export function parseRRule(value: string): RecurrenceRule | undefined {
  const parts: Record<string, string> = {};
  for (const segment of value.split(";")) {
    const [key, ...rest] = segment.split("=");
    if (!key || rest.length === 0) continue;
    parts[key.toUpperCase()] = rest.join("=");
  }

  const frequency = parts.FREQ?.toLowerCase();
  if (
    frequency !== "daily" &&
    frequency !== "weekly" &&
    frequency !== "monthly"
  ) {
    return undefined;
  }

  const interval = Math.max(1, Number(parts.INTERVAL || "1") || 1);
  const untilUtc = parts.UNTIL ? parseIcsUtcStamp(parts.UNTIL) : undefined;

  return untilUtc
    ? { enabled: true, frequency, interval, untilUtc }
    : { enabled: true, frequency, interval };
}

/** `YYYYMMDDTHHMMSSZ` (or the date-only form) back to an ISO instant. */
export function parseIcsUtcStamp(value: string): string | undefined {
  const timed = value.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/);
  if (timed) {
    const [, year, month, day, hour, minute, second] = timed;
    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      )
    ).toISOString();
  }

  const dateOnly = value.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(
      Date.UTC(Number(year), Number(month) - 1, Number(day))
    ).toISOString();
  }

  return undefined;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Offset in minutes that `timeZone` is ahead of UTC at `instant`. */
function offsetMinutesAt(instant: Date, timeZone: string): number {
  const parts = businessDateParts(instant, timeZone);
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return Math.round((asIfUtc - instant.getTime()) / 60000);
}

/** `-0500` / `+0000` — the ICS UTC-offset form. */
function formatUtcOffset(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const absolute = Math.abs(minutes);
  return `${sign}${pad2(Math.floor(absolute / 60))}${pad2(absolute % 60)}`;
}

function shortZoneName(instant: Date, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(instant);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

export type ZoneTransition = {
  /** First instant on the NEW offset. */
  at: Date;
  offsetFromMinutes: number;
  offsetToMinutes: number;
  name: string;
};

/**
 * Offset changes in `timeZone` between two instants.
 *
 * Scans day by day for a change, then binary-searches that day down to the
 * minute. Day-granularity scanning is safe because no real zone changes offset
 * twice within a day, and it keeps a ten-year invite cheap (about 3,650 Intl
 * reads, not 5 million).
 */
export function findZoneTransitions(
  timeZone: string,
  fromUtc: Date,
  toUtc: Date
): ZoneTransition[] {
  const transitions: ZoneTransition[] = [];
  if (!(fromUtc.getTime() < toUtc.getTime())) return transitions;

  let cursorMs = fromUtc.getTime();
  let previousOffset = offsetMinutesAt(new Date(cursorMs), timeZone);
  const endMs = toUtc.getTime();

  while (cursorMs < endMs) {
    const nextMs = Math.min(cursorMs + DAY_MS, endMs);
    const nextOffset = offsetMinutesAt(new Date(nextMs), timeZone);

    if (nextOffset !== previousOffset) {
      // Binary-search the day down to the second, then snap: a real zone
      // changes offset on a minute boundary, and the scan cursor rarely sits on
      // one, so an unsnapped answer carries seconds that never existed.
      let low = cursorMs;
      let high = nextMs;
      while (high - low > 1_000) {
        const mid = low + Math.floor((high - low) / 2);
        if (offsetMinutesAt(new Date(mid), timeZone) === previousOffset) {
          low = mid;
        } else {
          high = mid;
        }
      }
      const at = new Date(Math.round(high / 60_000) * 60_000);
      transitions.push({
        at,
        offsetFromMinutes: previousOffset,
        offsetToMinutes: nextOffset,
        name: shortZoneName(at, timeZone),
      });
      previousOffset = nextOffset;
    }

    cursorMs = nextMs;
  }

  return transitions;
}

/**
 * A VTIMEZONE component defining `timeZone` over the span an event covers.
 *
 * Observances are listed EXPLICITLY (one per real transition) rather than as
 * RRULEs: the transition rules of a zone change by legislation, and a file that
 * states the offsets it means cannot be wrong later. `spanEndUtc` bounds how
 * far to enumerate — an open-ended series gets `maxYears` worth, which every
 * calendar client extrapolates from its own zone database anyway.
 *
 * Returns undefined for UTC, which needs no definition.
 */
export function buildVTimezone(
  timeZone: string,
  spanStartUtc: string,
  spanEndUtc?: string,
  options?: { maxYears?: number }
): string[] | undefined {
  const zone = resolveEventTimeZone(timeZone);
  if (zone === "UTC") return undefined;

  const maxYears = options?.maxYears ?? 5;
  const startMs = new Date(spanStartUtc).getTime();
  if (Number.isNaN(startMs)) return undefined;

  // Start a year early so the observance in force AT the event is included.
  const scanStart = new Date(startMs - 366 * DAY_MS);
  const requestedEndMs = spanEndUtc ? new Date(spanEndUtc).getTime() : Number.NaN;
  const cappedEndMs = startMs + maxYears * 366 * DAY_MS;
  const scanEnd = new Date(
    Number.isNaN(requestedEndMs)
      ? cappedEndMs
      : Math.min(Math.max(requestedEndMs, startMs), cappedEndMs)
  );

  const transitions = findZoneTransitions(zone, scanStart, scanEnd);

  const lines = ["BEGIN:VTIMEZONE", `TZID:${zone}`];

  if (transitions.length === 0) {
    // A zone with no offset change in the span still has to be defined.
    const offset = offsetMinutesAt(new Date(startMs), zone);
    lines.push(
      "BEGIN:STANDARD",
      `DTSTART:${formatIcsLocal(scanStart.toISOString(), zone)}`,
      `TZOFFSETFROM:${formatUtcOffset(offset)}`,
      `TZOFFSETTO:${formatUtcOffset(offset)}`,
      ...(shortZoneName(new Date(startMs), zone)
        ? [`TZNAME:${shortZoneName(new Date(startMs), zone)}`]
        : []),
      "END:STANDARD"
    );
  } else {
    for (const transition of transitions) {
      const daylight = transition.offsetToMinutes > transition.offsetFromMinutes;
      const component = daylight ? "DAYLIGHT" : "STANDARD";
      // DTSTART of an observance is the wall clock in the OUTGOING offset.
      const localStart = new Date(
        transition.at.getTime() + transition.offsetFromMinutes * 60_000
      );
      const stamp =
        `${localStart.getUTCFullYear()}${pad2(localStart.getUTCMonth() + 1)}` +
        `${pad2(localStart.getUTCDate())}T${pad2(localStart.getUTCHours())}` +
        `${pad2(localStart.getUTCMinutes())}${pad2(localStart.getUTCSeconds())}`;

      lines.push(
        `BEGIN:${component}`,
        `DTSTART:${stamp}`,
        `TZOFFSETFROM:${formatUtcOffset(transition.offsetFromMinutes)}`,
        `TZOFFSETTO:${formatUtcOffset(transition.offsetToMinutes)}`,
        ...(transition.name ? [`TZNAME:${transition.name}`] : []),
        `END:${component}`
      );
    }
  }

  lines.push("END:VTIMEZONE");
  return lines;
}
